import {
  array,
  boolean,
  InferInput,
  InferOutput,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  ValiError,
} from 'valibot';

import { PlaylistItem, PlaylistItemSchema } from '../apiSchema.ts';

export const PlaylistsIdRequestArgsSchema = object({
  id: string(),
  isLibrary: optional(boolean()),
});

export const PlaylistsIdResponseSchema = pipe(
  object({
    data: array(PlaylistItemSchema),
  }),
  transform(({ data }) => {
    return {
      ...data,
    };
  }),
);

export const PlaylistsIdAPISchema = pipe(
  object({
    data: PlaylistsIdResponseSchema,
  }),
);

export type PlaylistsIdRequestArgs = InferOutput<
  typeof PlaylistsIdRequestArgsSchema
>;

export type PlaylistsIdResponse = InferOutput<
  typeof PlaylistsIdResponseSchema
>;

export type PlaylistsIdAPIInput = InferInput<
  typeof PlaylistsIdAPISchema
>;

export type PlaylistsIdAPI = InferOutput<
  typeof PlaylistsIdAPISchema
>;

/**
 * Fetch a specific playlist by ID from the user's library or the catalog data.
 * @param {MusicKit.MusicKitInstance} player The MusicKit instance.
 * @param {PlaylistsIdRequestArgs} args The request arguments.
 * @returns {Promise<PlaylistItem>} The playlist data.
 */
export const $getPlaylistsId = async (
  player: MusicKit.MusicKitInstance,
  args: PlaylistsIdRequestArgs,
): Promise<PlaylistItem> => {
  const p = safeParse(
    PlaylistsIdRequestArgsSchema,
    args,
  );
  if (!p.success) {
    throw new ValiError(p.issues);
  }
  const { id, isLibrary } = p.output;
  const library = isLibrary ?? true;
  const endpoint = library
    ? `/v1/me/library/playlists/${id}`
    : `/v1/catalog/${player.storefrontId}/playlists/${id}`;

  const res = await player.api.music(endpoint);
  const parsed = safeParse(
    PlaylistsIdAPISchema,
    res,
  );
  if (!parsed.success) {
    console.error(
      `[${endpoint}] Validation failed:`,
      JSON.stringify(parsed.issues, null, 2),
    );
    throw new ValiError(parsed.issues);
  }
  const data: PlaylistsIdResponse = parsed.output.data;
  const playlist = data[0];
  if (!playlist) {
    // NOTE: This route should never be reached. If the data does not exist, an MKError should occur.
    throw new Error(`Playlist with ID ${id} not found`);
  }

  return playlist;
};
